"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { MentionBox } from "@/components/ui/mention-box";
import { getProfile } from "@/lib/account/profile";
import { addComment, editComment } from "@/lib/blog/comments";
import type { BlogPostComment, EnrichedProfile } from "@/lib/supabase/types";
import { cn, scrollToElement } from "@/lib/utils";
import { CheckIcon, UserIcon } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import type React from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { CommentTreeNode } from "./comments-box";

interface CommentFormProps {
	currentUserId: string | null;
	postId: string;
	comment?: string;
	showAvatar?: boolean;
	editCommentId?: string;
	parentCommentId?: string;
	onSubmit?: (comment?: CommentTreeNode) => void;
	onReplyAdded?: (parentCommentId: string, reply: CommentTreeNode) => void;
}

export function CommentForm({
	currentUserId,
	postId,
	comment,
	showAvatar = true,
	editCommentId,
	parentCommentId,
	onSubmit,
	onReplyAdded,
}: CommentFormProps) {
	const router = useRouter();

	const [profile, setProfile] = useState<EnrichedProfile | null>(null);
	const [commentText, setCommentText] = useState<string | null>(comment || "");
	const [hasError, setHasError] = useState(false);
	const [isEditing, setIsEditing] = useState(!!editCommentId);

	const cancelable = !!editCommentId || !!parentCommentId;

	useEffect(() => {
		const fetchProfile = async () => {
			try {
				if (!currentUserId) return;
				const { data, error } = await getProfile(currentUserId);
				if (error) throw error;

				setProfile(data);
			} catch (error) {
				console.error(error);
				toast("Are you sure you are logged in?", {
					icon: <UserIcon className="w-4 h-4" />,
					style: {
						background: "red",
						color: "white",
					},
				});
			}
		};

		fetchProfile();
	}, [currentUserId]);

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!currentUserId) {
			toast("User not logged in.. Please login to comment.", {
				icon: <UserIcon className="w-4 h-4" />,
				style: {
					background: "yellow",
					color: "black",
				},
			});
			setHasError(true);
			router.push("/auth/sign-in");
			return;
		}

		if (!commentText) {
			toast("Comment is empty.. Please enter a comment.", {
				style: {
					background: "red",
					color: "white",
				},
			});
			setHasError(true);
			return;
		}

		if (isEditing) {
			if (!editCommentId) {
				toast("Comment not found.. Please try again.", {
					style: {
						background: "red",
						color: "white",
					},
				});
				setHasError(true);
				return;
			}

			// Edit comment
			const { data, error } = await editComment(editCommentId, commentText);
			if (error || !data) {
				toast("Error editing comment.. Please try again.", {
					style: {
						background: "red",
						color: "white",
					},
				});
				setHasError(true);
				return;
			}

			toast("Comment edited successfully", {
				icon: <CheckIcon className="w-4 h-4" />,
				style: {
					background: "green",
					color: "white",
				},
			});

			// Delay scrolling to allow the new comment to be rendered
			setTimeout(() => {
				scrollToElement(`comment-${(data as BlogPostComment).id}`);
			}, 1000);
		} else {
			// Add comment
			const { data, error } = await addComment(
				currentUserId,
				postId,
				commentText,
				parentCommentId,
			);
			if (error || !data) {
				toast("Error adding comment.. Please try again.", {
					style: {
						background: "red",
						color: "white",
					},
				});
				setHasError(true);
				return;
			}

			toast("Comment added successfully", {
				icon: <CheckIcon className="w-4 h-4" />,
				style: {
					background: "green",
					color: "white",
				},
			});

			// Delay scrolling to allow the new comment to be rendered
			setTimeout(() => {
				scrollToElement(`comment-${(data as BlogPostComment).id}`);
			}, 1000);

			setCommentText("");

			// Create CommentTreeNode from the new comment
			const commentTreeNode: CommentTreeNode = {
				...(data as BlogPostComment),
				replies: [],
				user: profile,
			};
			onSubmit?.(commentTreeNode);

			// If this is a reply, also call onReplyAdded
			if (parentCommentId && onReplyAdded) {
				onReplyAdded(parentCommentId, commentTreeNode);
			}

			return;
		}

		setCommentText("");
		onSubmit?.();

		router.refresh();
	};

	const handleCancel = () => {
		setCommentText("");
		setIsEditing(false);
		onSubmit?.();
		router.refresh();
	};

	return (
		<form className="flex flex-col gap-2 my-12 w-full" onSubmit={handleSubmit}>
			<div className="flex flex-row gap-2">
				{showAvatar && profile?.avatar_url && (
					<Avatar>
						<AvatarImage
							src={profile.avatar_url}
							alt={"User avatar"}
							className={cn(
								"size-10 rounded-full transition-all border border-primary/20",
								"hover:border-primary hover:scale-102",
							)}
						/>
						<AvatarFallback>ZM</AvatarFallback>
					</Avatar>
				)}
				<Field className="w-full py-2 px-4 bg-background mb-4 rounded-lg rounded-t-lg border border-primary-34">
					<FieldLabel htmlFor="comment" className="sr-only">
						Your comment
					</FieldLabel>
					<MentionBox
						id="comment"
						rows={6}
						required
						className={cn(
							"w-full px-0 text-sm bg-background text-primary border-none shadow-none",
							"focus:ring-0 focus:outline-none transition-all",
						)}
						placeholder="Write a comment..."
						value={commentText || ""}
						onValueChange={(e) => setCommentText(e)}
					/>
					<p className="text-xs text-primary-34">
						By commenting you're agreeing to our{" "}
						<Link href="/terms" className="text-primary text-xs">
							Community Guidelines
						</Link>
					</p>
				</Field>
			</div>
			<div className="flex flex-row w-full justify-end items-end gap-2">
				{cancelable && (
					<Button
						type="button"
						variant="outline"
						className={cn(
							"relative border-none shadow-none bg-transparent justify-end self-end align-end font-bold items-center py-1.5 px-3 text-md text-center",
							"text-primary rounded-full transition-all",
						)}
						onClick={handleCancel}
					>
						Cancel
					</Button>
				)}
				<Button
					type="submit"
					className={cn(
						"relative justify-end self-end align-end font-bold items-center py-1.5 px-3 text-md text-center",
						"text-background bg-primary rounded-full",
						"transition-all focus:ring-4 focus:ring-primary-200",
						"ring-offset-2 transition duration-200 hover:ring-2 hover:ring-green-500",
					)}
				>
					Comment
				</Button>
			</div>
		</form>
	);
}
